<template>
  <motlet-neo :options="motletOptions">
    <template v-slot:content="{ props }">
      <line-chart :settings="settings" :motlet="props.motlet" :store-name="storeName" :active-increment="activeIncrement" />
    </template>
  </motlet-neo>
</template>

<script>
import { merge, isEmpty } from 'lodash';
// BEGIN::Added.by.KWShin.2024.09.26.
import bus from '@/utils/bus';
// FINISH::Added.by.KWShin.2024.03.06.
// BEGIN::Added.by.SHJ.2026.04.27.
import { api } from '@/api/common/instances';
// FINISH::Added.by.SHJ.2026.04.27.
import lineChartable from './lineChartable';

export default {
  mixins: [lineChartable],
  data() {
    return {
      motletOptions: {
        id: 'dv/abap/os/ntwk',
        title: 'dv.title.ntwk',
        monitor: { monitorCode: 'C_ABAP_OS_NTWK', eventCode: 'ABAP_OS_NTWK', points: 60 },
        settings: { chart: { use: true } },
      },
      socketData: [],
    };
  },
  watch: {
    sstmId: {
      handler() {
        this.sendMessage();
      },
    },
    activeIncrement: {
      immediate: true,
      handler(value) {
        if (value) this.sendMessage();
      },
    },
    // BEGIN::Added.by.SHJ.2026.05.13. 언어 변경 시 헤더 타이틀 재합성
    '$i18n.locale'() {
      const field = this.motletOptions.settings.chart.field;
      if (field && field.code) this.chartFieldChanged(field.code);
    },
    // FINISH::Added.by.SHJ.2026.05.13.
  },
  methods: {
    wsBufferedDataHandler(key, responses) {
      const [response] = responses;
      const bound = response.length < this.monitor.points ? response.length : this.monitor.points;
      const data = response.slice(response.length - bound).map(item => {
        return this.dataHandler(item.data, item.ctime, item.empty);
      });
      this.socketData.push({
        id: key.instId,
        data,
      });
      if (this.socketData.length == this.descendants.length) {
        this.data = merge([], this.socketData);
        this.socketData = [];
        this.prepend(['packets', 'inPackets', 'outPackets', 'inErrors', 'outErrors', 'collisions']);
        // BEGIN::Added.by.KWShin.2024.09.26.
        if (this.data && this.data.length > 0) {
          bus.$emit(`change:timestamp-${this.storeName}`, {
            eventCode: this.motletOptions.monitor.eventCode,
            timestamp: this.data[this.data.length - 1].ctime,
          });
        }
        // FINISH::Added.by.KWShin.2024.09.26.
        // BEGIN::Added.by.SHJ.2026.04.27.
        // WS 버퍼가 차트 X축 개수(monitor.points)만큼 채우지 못한 경우 앞쪽 빈 구간을 DB에서 보충한다.
        this.backfillFromDb();
        // FINISH::Added.by.SHJ.2026.04.27.
      }
    },
    wsDataHandler(key, response, ctime, empty) {
      const data = this.dataHandler(response, ctime, empty);
      this.socketData.push({
        id: key.instId,
        data,
      });
      if (this.socketData.length == this.descendants.length) {
        this.socketData.forEach(s => {
          this.data.find(d => d.id == s.id).data.push(s.data);
        });
        this.socketData = [];
        this.prepop();
      }
    },
    dataHandler(response, ctime, empty) {
      if (isEmpty(response)) response = [{ stime: ctime }];
      const data = {
        ctime: parseInt(ctime) - (parseInt(ctime) % this.monitor.cycle), // db 데이터 간격 기준으로 실시간 데이터를 근사값으로 보정,
        stime: parseInt(response[0].stime),
        inPackets: '',
        outErrors: '',
        outPackets: '',
        packets: '',
        inErrors: '',
        collisions: '',
      };
      if (!empty) {
        data.packets = 0;
        data.inPackets = 0;
        data.outPackets = 0;
        data.inErrors = 0;
        data.outErrors = 0;
        data.collisions = 0;
        response.forEach(r => {
          // BEGIN:뭔가 mistyping 인듯.... 수정함.2022.07.11.KWShin.
          //data.writes += parseInt(r.writes);
          data.packets += parseInt(r.packets);
          // FINISH:뭔가 mistyping 인듯.... 수정함.2022.07.11.KWShin.
          data.inPackets += parseInt(r.inPackets);
          data.outPackets += parseInt(r.outPackets);
          data.inErrors += parseInt(r.inErrors);
          data.outErrors += parseInt(r.outErrors);
          data.collisions += parseInt(r.collisions);
        });
      }
      return data;
    },
    // BEGIN::Added.by.SHJ.2026.04.27.
    /**
     * WS 버퍼가 monitor.points 만큼 채워지지 않았을 때 DB에서 부족한 앞 구간을 조회해
     * 이미 비어있는 슬롯(packets===undefined)에만 값을 주입한다. 실제 WS 데이터는 덮어쓰지 않는다.
     */
    async backfillFromDb() {
      if (!this.data || this.data.length === 0) return;
      const firstInst = this.data[0].data;
      if (!firstInst || firstInst.length === 0) return;
      const realPoints = firstInst.filter(d => d.packets !== undefined && d.packets !== '');
      if (realPoints.length === firstInst.length) return;
      const beginYmdhms = firstInst[0].ctime;
      const endYmdhms = realPoints.length > 0 ? realPoints[0].ctime - this.monitor.cycle : firstInst[firstInst.length - 1].ctime;
      if (endYmdhms < beginYmdhms) return;
      const params = {
        beginYmdhms,
        endYmdhms,
        sstmId: this.sstmId,
        motletCode: this.monitor.monitorCode,
        ...this.dataParam,
      };
      try {
        const { data: results } = await api.get('/motlet-data/chart', { params });
        if (!results || results.length === 0) return;
        results.forEach(result => {
          const inst = this.data.find(d => d.id === result.key.instId);
          if (!inst) return;
          result.data.forEach(db => {
            const dbCtime = parseInt(db.ctime);
            const slot = inst.data.find(p => p.ctime === dbCtime);
            if (slot && (slot.packets === undefined || slot.packets === '')) {
              slot.packets = parseInt(db.packets);
              slot.inPackets = parseInt(db.inPackets);
              slot.outPackets = parseInt(db.outPackets);
              slot.inErrors = parseInt(db.inErrors);
              slot.outErrors = parseInt(db.outErrors);
              slot.collisions = parseInt(db.collisions);
              if (db.stime) slot.stime = parseInt(db.stime);
            }
          });
        });
      } catch (e) {
        // DB 조회 실패 시 차트는 WS 실시간 수신으로 점진적으로 채워지므로 무시한다.
        console.warn('[Network] backfillFromDb failed', e);
      }
    },
    // FINISH::Added.by.SHJ.2026.04.27.
    sendMessage() {
      this.$websocket.realtime.send({
        requests: this.descendants.map(descendant => ({
          key: { name: 'rawOsNtwk', sstmId: descendant.systemId, instId: descendant.id },
          fields: ['stime', 'packets', 'inPackets', 'outPackets', 'inErrors', 'outErrors', 'collisions'],
          handler: {
            buffer: this.wsBufferedDataHandler,
            every: this.wsDataHandler,
          },
        })),
      });
    },
    /**
     * Added.by.KWShin.20204.09.26.
     * Comment는 PcapMmory.vue 를 참조할 것.
     */
    chartFieldChanged(code) {
      this.motletOptions.settings.chart.field = this.chartFields.find(f => f.code == code);
      this.setChartField(code);
      // 차트 항목이 변경되었으므로 서브타이틀을 바꾸어 준다.2026.06.16.
      this.motletOptions.subTitle = `(${this.motletOptions.settings.chart.field.text})`;
    },
  },
  created() {
    // BEGIN::Added.by.KWShin.2024.09.26.
    bus.$on(`update:chartFieldCode-${this.motletOptions.id}`, chartFieldCode => {
      this.chartFieldChanged(chartFieldCode);
    });
    // FINISH::Added.by.KWShin.2024.09.26.
  },
  destroyed() {
    // BEGIN::Added.by.KWShin.2024.09.26.
    bus.$off(`update:chartFieldCode-${this.motletOptions.id}`);
    // FINISH::Added.by.KWShin.2024.09.26.
  },
};
</script>
